/*
 * Copyright (c) 2023 by N-Ach (Pvt) Ltd
 *
 * ╔═╗ ╔╗    ╔═══╗  ╔╗
 * ║║╚╗║║    ║╔═╗║  ║║
 * ║╔╗╚╝║    ║║ ║╠══╣╚═╗
 * ║║╚╗║║╔══╗║╚═╝║╔═╣╔╗║
 * ║║ ║║║╚══╝║╔═╗║╚═╣║║║
 * ╚╝ ╚═╝    ╚╝ ╚╩══╩╝╚╝
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information
 * of N-Ach (Pvt) Ltd. You shall not disclose such Confidential
 * Information and shall use it only in accordance with the terms
 * of the license agreement you entered into with N-Ach (Pvt) Ltd.
 *
 *
 * @Author Dilhari Jayathunga
 * @Date 23/11/2023
 */

import {
  BookmarkBorder,
  Dashboard,
  Face,
  Logout,
  MenuOpen,
  Place,
} from "@mui/icons-material";
import { Button, ListItemIcon, Menu, MenuItem } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { logout } from "../../Store/Slices/authSlice";
import { useDispatch } from "react-redux";
import styles from "./NavBar.module.css";
import { useLazyGetProfileDetailsQuery } from "../../Store/Services/ProfileService";
import { useNavigate } from "react-router";
import SignUpDialog from "../SignUp/SignUpDialog";
import LoginDialog from "../Login/LoginDialog";
import GuestDialog from "../Auth/GuestDialog";
import { toggleLoginDialog, toggleRegisterDialog } from "../../Store/Slices/authSlice";

const NavUser = ({ open, onClose }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeDialog, setActiveDialog] = useState(null);
  const [openGuest, setOpenGuest] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleSignInClick = () => {
    dispatch(toggleLoginDialog());
    if (onClose) onClose();
    handleClose();
  };

  const handleSignUpClick = () => {
    dispatch(toggleRegisterDialog());
    if (onClose) onClose();
    handleClose();
  };

  const [getDetails, { data: profileDetails, isLoading, isSuccess }] =
    useLazyGetProfileDetailsQuery();

  const isSignIn = sessionStorage.getItem("isSignedIn");

  useEffect(() => {
    const callApi = async () => {
      try {
        await getDetails().unwrap();
      } catch (error) {
        console.error("Error fetching profile details:", error);
      }
    };
    if (isSignIn == 1) {
      setTimeout(callApi, 500);
    }
  }, [isSignIn]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const openLogin = () => {
    setActiveDialog("login");
    handleClose();
  };

  const openSignUp = () => {
    setActiveDialog("signup");
    handleClose();
  };

  const closeDialog = () => {
    setActiveDialog(null);
  };

  const onLogout = () => {
    const location = window.location.pathname;
    dispatch(logout());
    if (location.startsWith("/admin")) {
      navigate("/");
    } else {
      window.location.reload();
    }
  };

  const roles = sessionStorage.getItem("roles");

  return (
    <div>
      {isSignIn != 1 ? (
        <Button
          variant="outlined"
          className={styles.button}
          onClick={handleClick}
        >
          Sign In
          <MenuOpen style={{ paddingLeft: "1rem" }} />
        </Button>
      ) : (
        <Button
          variant="outlined"
          className={styles.button}
          onClick={handleClick}
        >
          {isLoading
            ? "..."
            : isSuccess
              ? (profileDetails?.firstName ? profileDetails?.firstName : profileDetails?.email)
              : "User"}
          <MenuOpen style={{ paddingLeft: "1rem" }} />
        </Button>
      )}

      <Menu
        anchorEl={anchorEl}
        id="user-menu"
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {!isSignIn
          ? [
            <MenuItem key="login" className={styles.menuItem} onClick={handleSignInClick}>
              Log in
            </MenuItem>,
            <MenuItem key="signup" className={styles.menuItem} onClick={handleSignUpClick}>
              Sign Up
            </MenuItem>,
          ]
          : [
            <MenuItem key="profile" onClick={handleClose}>
              <Link className={styles.menuItem} to="/profile/overview">
                <ListItemIcon>
                  <Face fontSize="small" />
                </ListItemIcon>
                Profile
              </Link>
            </MenuItem>,
            <MenuItem key="bookings" onClick={handleClose}>
              <Link className={styles.menuItem} to="/profile/requests">
                <ListItemIcon>
                  <BookmarkBorder fontSize="small" />
                </ListItemIcon>
                Bookings
              </Link>
            </MenuItem>,
            <MenuItem key="addresses" onClick={handleClose}>
              <Link className={styles.menuItem} to="/profile/address">
                <ListItemIcon>
                  <Place fontSize="small" />
                </ListItemIcon>
                Addresses
              </Link>
            </MenuItem>,
            roles?.split(",").includes("PRIV_ES_ADMIN_DISPLAY") && (
              <MenuItem key="admin" onClick={handleClose}>
                <Link className={styles.menuItem} to="/admin">
                  <ListItemIcon>
                    <Dashboard fontSize="small" />
                  </ListItemIcon>
                  Admin Dashboard
                </Link>
              </MenuItem>
            ),
            <MenuItem key="logout" onClick={onLogout} className={styles.menuItem}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>,
          ]}
      </Menu>


      <SignUpDialog open={activeDialog === "signup"} onClose={closeDialog} />
      <LoginDialog open={activeDialog === "login"} onClose={closeDialog} onCloseFinal={() => { }} />
    </div>
  );
};

export default NavUser;
